import { useContext, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { Button } from "iuvity-ui-components/dist/button";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { CustomInput } from "../inputs/CustomInput";
import { NewDialogProps } from "../dialog/NewCustomDialog";
import { useVersions } from "../../hooks/useVersions";
import { useTranslations } from "../../hooks/useTranslations";
import { convertValueTypes } from "../../utils";
import { ProdGeneralInfoFormSchema } from "../../validations/schemas";
import { ErrorMessage } from "@hookform/error-message";
import { AppContext } from "../../context/index";
import { FieldsLinkedTocreateLabelsButton, REVALIDATE_FORM_PROPS } from "../../constants";
import { createLabelButtonMaker } from "../../helpers/createLabelButtonMaker";

interface ProdGeneralInfoProps {
  type: "PRODUCT" | "PROPERTIES";
  data: any;
  actionFn: Function;
  auxModalController: React.Dispatch<React.SetStateAction<NewDialogProps>>;
  dropdownFilterObject?: any;
}

export const ProdGeneralInfo: React.FC<ProdGeneralInfoProps> = ({
  type,
  data,
  actionFn,
  auxModalController,
  dropdownFilterObject,
}) => {
  const {
    t,
    appState: { user, selectedProduct },
  } = useContext(AppContext);

  const navigate = useNavigate();

  const { data: versionsData } = useVersions(
    type === "PRODUCT" ? "DATA" : "ACTIONS"
  );
  const { data: labelsData, reloadFn } = useTranslations("DATA");

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { errors, dirtyFields, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });

  const onSave = async () => {
    const formValues = getValues();
    const dirtyFieldsKeys: any[] = Object.keys(dirtyFields);
    const valuesToUpdate = Object.keys(formValues)
      .filter((key) => dirtyFieldsKeys.includes(key))
      .reduce((cur, key) => {
        const value =
          key === "status"
            ? formValues[key]
              ? "ACTIVE"
              : "INACTIVE"
            : formValues[key];
        return Object.assign(cur, { [key]: value });
      }, {});

    if (type === "PRODUCT") {
      await actionFn("UPDATE", [
        {
          id: selectedProduct?.id,
          updatedby: user?.name,
          ...valuesToUpdate,
        },
      ]);
    } else if (type === "PROPERTIES") {
      const propsToUpdate = Object.keys(valuesToUpdate).map((key) => ({
        id: data.find((prop: any) => key === prop?.key)!.id,
        value: (valuesToUpdate as any)[key],
      }));
      await actionFn("PROPERTY", "UPDATE", propsToUpdate);
    }

    reset(formValues);
  };

  const onCancel = () => {
    navigate("/products");
  };

  const handleModalProps = (isCancelButton?: boolean) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: isCancelButton
        ? t("Products.cancelModalTitle")
        : t("Products.saveModalTitle"),
      content: isCancelButton
        ? t("Products.cancelModalContent")
        : t("Products.saveModalContent"),
      onConfirm: isCancelButton ? onCancel : onSave,
    }));
  };

  const formOptions = useMemo(() => {
    if (versionsData && labelsData) {
      return {
        releaseversion: versionsData
          .map((version: any) => ({
            label: version.name,
            value: version.name,
          }))
          .sort((a: any, b: any) => b.label.localeCompare(a.label)),
        labelkey: labelsData?.filter((item: any) => item?.widgets)
          ?.map((item: any)=> ({ label: item?.key, value: item?.key}))
          ?.sort((a: any, b: any) => a.label.localeCompare(b.label)),
      };
    } else {
      return null;
    }
  }, [versionsData, labelsData]);

  useEffect(() => {
    if (data && data.length > 0) {
      const initialValues = data
        .map((property: any) => {
          const valKey = property.key.split(".").pop() || "";
          return {
            [valKey]: convertValueTypes(property.value),
          };
        })
        .reduce((acc: any, current: any) => ({
          ...acc,
          ...current,
        }));

      reset(initialValues);
    }
  }, [data, reset]);

  return (
    (type === "PRODUCT" ? data && formOptions : data) && (
      <BoxFull className="px-0">
        <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
          {Object.keys(getValues()).length > 0 && (
            <>
              {data.map((property: any) => {
                return (
                  <div
                    key={`c-${property.key}`}
                    className="flex flex-column mb-3"
                    style={{ width: "50%" }}
                  >
                    <Controller
                      key={property.key}
                      name={property.key}
                      control={control}
                      rules={
                        (ProdGeneralInfoFormSchema(t) as any)[property.key]
                      }
                      render={({ field }) => {
                        let extraProps = {
                          keyfilter:
                            property?.type === "number"
                              ? /^[0-9]/
                              : (ProdGeneralInfoFormSchema(t) as any)[
                                  property.key
                                ]?.keyfilter,
                          maxLength: (ProdGeneralInfoFormSchema(t) as any)[
                            property.key
                          ]?.maxLength?.value,
                        };

                        return ["releaseversion", "labelkey"].includes(property.key) ? (
                          // @ts-ignore
                          <>
                            <Dropdown
                              className="dropdown mb-3"
                              label={property?.label}
                              options={(formOptions as any)[property.key]}
                              filter={!!dropdownFilterObject ? (dropdownFilterObject[property.key] || false) : false}
                              showClear={false}
                              width="20rem"
                              {...field}
                            />
                            {
                                FieldsLinkedTocreateLabelsButton.includes(property.key) &&
                                createLabelButtonMaker({
                                  setModalProps: auxModalController,
                                  dialogTitle: t('ConsoleTranslations.createLabelTitle'),
                                  dialogDetail: t('ConsoleTranslations.createLabelDescription'),
                                  buttonLabel: t('ConsoleTranslations.createLabelTitle'),
                                  functionality:'Products',
                                  actionsToExecute:(key: string)=>{
                                    reloadFn();
                                    setValue("labelkey", key, REVALIDATE_FORM_PROPS)
                                  },
                                })
                            }
                          </>
                        ) : (
                          <CustomInput
                            id={`input-${property.key}`}
                            key={property.key}
                            className={`${
                              errors[property.key]?.message
                                ? "p-invalid"
                                : "input-md mb-4"
                            } ${property.key === "nameinapi" ? "rd-only" : ""}`}
                            label={property?.label}
                            placeholder={`${t(
                              "Products.propPlaceholder"
                            )} ${property?.label.toLowerCase()}`}
                            type={
                              property.key === "cardBackgroundColor"
                                ? "color"
                                : property?.type === "number"
                                ? "text"
                                : property?.type
                            }
                            autoComplete="off"
                            style={{
                              width: "20rem",
                              height: `${
                                property.key === "cardBackgroundColor" &&
                                "2.2rem"
                              }`,
                              paddingBottom: `${
                                property.key === "cardBackgroundColor" && "0rem"
                              }`,
                            }}
                            {...field}
                            {...extraProps}
                            readOnly={property.key === "nameinapi"}
                          />
                        );
                      }}
                    />
                    {property.type !== "boolean" && (
                      <ErrorMessage
                        errors={errors}
                        name={property.key}
                        //@ts-ignore
                        render={({ message }) => (
                          <small
                            className="p-error block"
                            style={{
                              marginTop: "0.5rem",
                              marginBottom: "1.5rem",
                            }}
                          >
                            {message}
                          </small>
                        )}
                      />
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div className="ap-selector-btn-c mt-2 gap-5">
          <Button
            variant="p-button-link"
            label={t("Products.cancelBtnLabel")}
            onClick={!isDirty ? onCancel : () => handleModalProps(true)}
          />
          <Button
            variant="p-button"
            label={t("Products.saveBtnLabel")}
            disabled={!isDirty || !isValid}
            onClick={() => handleModalProps()}
          />
        </div>
      </BoxFull>
    )
  );
};
